import { useMemo, useState } from "react";
import type { EngineRowDto } from "../ipc/types";

/** Página de resultados + controles para una tabla de motor (item 8: buscar/filtrar/paginar). */
export interface TableView {
  readonly query: string;
  readonly setQuery: (v: string) => void;
  readonly statusFilter: string;
  readonly setStatusFilter: (v: string) => void;
  /** Etiquetas de estado distintas presentes en los datos (para el desplegable de filtro). */
  readonly statuses: readonly string[];
  /** Filtro por rango de fechas (YYYY-MM-DD). Sólo aplica si las filas traen `at`. */
  readonly dateFrom: string;
  readonly setDateFrom: (v: string) => void;
  readonly dateTo: string;
  readonly setDateTo: (v: string) => void;
  /** ¿Hay fechas en los datos? (para mostrar u ocultar el filtro de fechas). */
  readonly hasDates: boolean;
  readonly page: number;
  readonly setPage: (n: number) => void;
  readonly pageCount: number;
  /** Filas visibles (ya filtradas y de la página actual). */
  readonly rows: readonly EngineRowDto[];
  /** Total tras filtrar (antes de paginar). */
  readonly filteredTotal: number;
  readonly total: number;
  readonly pageStart: number;
  readonly pageEnd: number;
}

const norm = (s: string): string => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export function useTableView(allRows: readonly EngineRowDto[], pageSize = 20): TableView {
  const [query, setQueryRaw] = useState("");
  const [statusFilter, setStatusFilterRaw] = useState("");
  const [dateFrom, setDateFromRaw] = useState("");
  const [dateTo, setDateToRaw] = useState("");
  const [page, setPage] = useState(0);

  // Cambiar búsqueda o filtro vuelve a la primera página.
  const setQuery = (v: string): void => { setQueryRaw(v); setPage(0); };
  const setStatusFilter = (v: string): void => { setStatusFilterRaw(v); setPage(0); };
  const setDateFrom = (v: string): void => { setDateFromRaw(v); setPage(0); };
  const setDateTo = (v: string): void => { setDateToRaw(v); setPage(0); };

  const statuses = useMemo(() => {
    const set = new Set<string>();
    for (const r of allRows) set.add(r.status.label);
    return [...set].sort();
  }, [allRows]);

  const hasDates = useMemo(() => allRows.some((r) => typeof r.at === "number"), [allRows]);

  const filtered = useMemo(() => {
    const q = norm(query.trim());
    // Rango [desde 00:00, hasta 23:59:59.999] en hora local.
    const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : -Infinity;
    const toMs = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : Infinity;
    const filterDates = dateFrom !== "" || dateTo !== "";
    return allRows.filter((r) => {
      if (statusFilter && r.status.label !== statusFilter) return false;
      if (filterDates && typeof r.at === "number" && (r.at < fromMs || r.at > toMs)) return false;
      if (filterDates && typeof r.at !== "number") return false; // sin fecha no puede cumplir el rango
      if (q.length === 0) return true;
      return norm(`${r.primary} ${r.secondary} ${r.amount} ${r.status.label}`).includes(q);
    });
  }, [allRows, query, statusFilter, dateFrom, dateTo]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = filtered.length === 0 ? 0 : safePage * pageSize + 1;
  const pageEnd = Math.min(filtered.length, (safePage + 1) * pageSize);
  const rows = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return {
    query, setQuery, statusFilter, setStatusFilter, statuses,
    dateFrom, setDateFrom, dateTo, setDateTo, hasDates,
    page: safePage, setPage, pageCount, rows,
    filteredTotal: filtered.length, total: allRows.length, pageStart, pageEnd,
  };
}
